import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { useViewType, ViewType } from '@eika/hooks';
import { Tabs } from '@eika/tabs';

import { useBSUDetails } from '../../fetchHooks/useAccount';
import useInterests from '../../fetchHooks/useInterests';
import useProfile from '../../fetchHooks/useProfile';
import useTranslation from '../../hooks/useTranslation';
import getBsuInterest from '../../utils/mapper/getBsuInterest';
import { AccountViewActions } from '../AccountViewActions';
import SkipLink from '../SkipLink/SkipLink';
import { Content } from '../shared/Content';
import { currencyFormatter } from '../shared/numberFormat';
import { BsuDetails, BsuDisplay, Props } from './AccountViewTypes';
import AccountViewWrapper from './AccountViewWrapper';
import AccountBalanceWrapper from './balance/AccountBalanceWrapper';
import AccountBSUDetails from './bsu/AccountBSUDetails';
import AccountBSUInterestSkeleton from './bsu/AccountBSUInterestSkeleton';
import getBSUDetails from './util/getBSUDetails';
import isBSUBlocked from './util/isBSUBlocked';

import './AccountView.scss';

const AccountView: React.FC<Props> = ({ account, className, isLoading = false }) => {
    const translated = useTranslation();
    const viewType = useViewType();

    const showBsuDetails = useMemo(() => account?.type === 'BSU', [account]);

    const [displayYearlyOrTotal, setDisplayYearlyOrTotal] = useState<BsuDisplay>(BsuDisplay.YEARLY);
    const { data: bsuDetails, error: bsuError } = useBSUDetails(account, showBsuDetails);
    const { data: interests, isLoading: loadingInterests } = useInterests(account?.accountId);
    const { data: userInfo } = useProfile();

    const isLoggedInUserAccountOwnerAndOlderThan34 = useMemo(
        () => isBSUBlocked(account, userInfo),
        [account, userInfo],
    );

    const defineDisplayTypes = (value: string) => setDisplayYearlyOrTotal(BsuDisplay[value as keyof typeof BsuDisplay]);

    const selectedBsuDetail: BsuDetails | undefined = useMemo(
        () => getBSUDetails(bsuDetails, account, showBsuDetails && !bsuError, displayYearlyOrTotal),
        [bsuDetails, showBsuDetails, displayYearlyOrTotal, account, bsuError],
    );

    const bsuInterest = useMemo(
        () => getBsuInterest(interests?.interestRates ?? [], account?.balance.accountingBalance ?? 0),
        [interests, account],
    );

    if (!account && !isLoading) {
        return (
            <Content>
                <h2>{translated.accountView.noAccount}</h2>
            </Content>
        );
    }

    return (
        <AccountViewWrapper
            useTabs={showBsuDetails && !bsuError && !isLoggedInUserAccountOwnerAndOlderThan34}
            onChangeValue={defineDisplayTypes}
            className={classNames('sgw-account-details', className)}
        >
            <dl className="sgw-account-details__wrapper">
                <AccountBalanceWrapper
                    account={account}
                    bsuDetails={selectedBsuDetail}
                    ageOver34={isLoggedInUserAccountOwnerAndOlderThan34}
                />
                {viewType === ViewType.desktop && !showBsuDetails && (
                    <SkipLink skipTo="#action-button-group" linkText={translated.accountSkipLink} />
                )}
                {showBsuDetails && !bsuError && !isLoggedInUserAccountOwnerAndOlderThan34 && (
                    <>
                        <Tabs.Tab value={BsuDisplay.YEARLY} className="account-view__bsu-details-container">
                            <AccountBSUDetails
                                term={translated.bsuDetails.saldo}
                                description={currencyFormatter(account?.balance?.accountingBalance ?? 0, 'NOK')}
                            />
                            <AccountBSUDetails
                                isLoading={loadingInterests}
                                term={translated.bsuDetails.interest}
                                description={bsuInterest}
                                skeleton={<AccountBSUInterestSkeleton />}
                            />
                            <AccountBSUDetails
                                term={translated.bsuDetails.interestThisYear}
                                description={currencyFormatter(interests?.notYetCapitalizedInterest ?? 0, 'NOK')}
                            />
                        </Tabs.Tab>
                        <Tabs.Tab value={BsuDisplay.TOTAL} className="account-view__bsu-details-container">
                            <AccountBSUDetails
                                term={translated.bsuDetails.maximumAmount}
                                description={currencyFormatter(bsuDetails?.maxAllowedDepositTotal ?? 0, 'NOK')}
                            />
                            <AccountBSUDetails
                                isLoading={loadingInterests}
                                term={translated.bsuDetails.interest}
                                description={bsuInterest}
                                skeleton={<AccountBSUInterestSkeleton />}
                            />
                            {typeof interests?.notYetCapitalizedInterest === 'number' && (
                                <AccountBSUDetails
                                    isLoading={loadingInterests}
                                    skeleton={<AccountBSUInterestSkeleton />}
                                    term={translated.bsuDetails.accruedInterest}
                                    description={currencyFormatter(interests.notYetCapitalizedInterest ?? 0, 'NOK')}
                                />
                            )}
                        </Tabs.Tab>
                    </>
                )}
                {isLoggedInUserAccountOwnerAndOlderThan34 && showBsuDetails && !bsuError && (
                    <div className="account-view__bsu-details-container">
                        <AccountBSUDetails
                            isLoading={loadingInterests}
                            term={translated.bsuDetails.interest}
                            description={bsuInterest}
                            skeleton={<AccountBSUInterestSkeleton />}
                        />
                        {typeof interests?.notYetCapitalizedInterest === 'number' && (
                            <AccountBSUDetails
                                isLoading={loadingInterests}
                                skeleton={<AccountBSUInterestSkeleton />}
                                term={translated.bsuDetails.accruedInterest}
                                description={currencyFormatter(interests.notYetCapitalizedInterest ?? 0, 'NOK')}
                            />
                        )}
                    </div>
                )}
            </dl>
            {viewType !== ViewType.desktop && !showBsuDetails && (
                <AccountViewActions className="account-view__actions-wrapper" />
            )}
        </AccountViewWrapper>
    );
};

export default AccountView;
